/**
 * A component that abstracts away the DOM API's "resize" event
 * handler and provides a convenient way to debounce it.
 *
 * @author    Frontend Design Systems <FrontendDesignSystems@wayfair.com>
 * @copyright 2019 Wayfair LLC - All rights reserved
 */
import {useEffect} from 'react';
import PropTypes from 'prop-types';
import debounce from '../debounce';

export default function ResizeListener({
  children,
  onResize,
  debounceRate,
  // Props used for dependecy injection
  addEventListener,
  removeEventListener,
}) {
  useEffect(() => {
    const debouncedResizeHandler = debounce(onResize, debounceRate);

    addEventListener('resize', debouncedResizeHandler, {passive: true});

    return () => {
      removeEventListener('resize', debouncedResizeHandler, {
        passive: true,
      });
    };
  }, [onResize, debounceRate, addEventListener, removeEventListener]);

  return children;
}

ResizeListener.propTypes = {
  // Any optional children to pass through and render
  children: PropTypes.node,
  // The amount of time the handler should be debounced
  debounceRate: PropTypes.number,
  // the handler called when the window resizes
  onResize: PropTypes.func.isRequired,
  // For unit tests / Dependency Injection
  addEventListener: PropTypes.func,
  removeEventListener: PropTypes.func,
};

ResizeListener.defaultProps = {
  debounceRate: 100,
  children: null,
  addEventListener(eventName, handler, options) {
    window.addEventListener(eventName, handler, options);
  },
  removeEventListener(eventName, handler, options) {
    window.removeEventListener(eventName, handler, options);
  },
};
